<!-- Champagne — you won. The bottle sprays across the screen with the winning
     lap time on the podium. -->
<template>
  <IonPage>
    <AppHeader :title="COPY.title" :back="backTo" />

    <IonContent :fullscreen="true" class="champ-content">
      <div class="champ">
        <canvas ref="canvas" class="champ-canvas" aria-hidden="true" />
        <div class="ion-padding champ-body">
          <template v-if="lap">
            <p class="champ-kicker">{{ COPY.kicker }}</p>
            <p class="champ-time">{{ lap.lapTime }}</p>
            <p class="champ-sub">{{ COPY.sub }}</p>
            <div class="podium" aria-hidden="true">
              <span class="step step--2">2</span><span class="step step--1">1</span><span class="step step--3">3</span>
            </div>
            <ActionButton :label="COPY.again" kind="primary" data-cta="again-btn" @click="raceAgain" />
            <ActionButton :label="COPY.garage" kind="secondary" data-cta="garage-btn" @click="openGarage" />
          </template>
          <EmptyState v-else :icon="trophyOutline" :title="COPY.missingTitle" :body="COPY.missingBody">
            <ActionButton :label="COPY.start" kind="primary" @click="raceAgain" />
          </EmptyState>
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IonPage, IonContent, onIonViewWillLeave } from '@ionic/vue';
import { trophyOutline } from 'ionicons/icons';
import { AppHeader, ActionButton, EmptyState, prefersReducedMotion } from '../core/ui/index.js';
import { findLap, saveLap } from '../store.js';
import { spray } from '../game/champagne.js';

const COPY = {
  title: 'Champagne',
  kicker: 'You won at Marina Bay',
  sub: 'Winning lap time, in the red Lamborghini Huracan.',
  again: 'Race again',
  garage: 'Open garage',
  start: 'Start lap',
  missingTitle: 'No win to celebrate yet',
  missingBody: 'Finish first and the champagne comes out here.',
};

const route = useRoute();
const router = useRouter();
const canvas = ref(null);
const lap = computed(() => {
  const l = findLap(route.params.id);
  return l && Number(l.place) === 1 ? l : null;
});
const backTo = router.hasRoute('grid') ? router.resolve({ name: 'grid' }).fullPath : '/';
let stopSpray = null;

onMounted(() => {
  if (!lap.value) return;
  if (lap.value.reward !== 'Champagne' && Number(lap.value.id) > 0) saveLap({ ...lap.value, reward: 'Champagne' });
  stopSpray = spray(canvas.value, { reducedMotion: prefersReducedMotion() });
});
const teardown = () => { stopSpray?.(); stopSpray = null; };
onIonViewWillLeave(teardown);
onBeforeUnmount(teardown);

const raceAgain = () => router.replace({ name: 'grid' });
const openGarage = () => router.push({ name: 'garage' });
</script>

<style scoped>
.champ { position: relative; min-height: 100%; }
.champ-canvas { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 1; }
.champ-body { position: relative; display: grid; gap: var(--sp-3); max-width: 520px; margin: 0 auto; text-align: center; }
.champ-kicker { margin: var(--sp-4) 0 0; font-weight: 700; color: var(--dim); text-transform: uppercase; letter-spacing: 0.06em; font-size: var(--text-sm); }
.champ-time { margin: 0; font-size: var(--text-hero); font-weight: 900; font-variant-numeric: tabular-nums; color: var(--ink); line-height: 1; }
.champ-sub { margin: 0; color: var(--dim); }
.podium { display: flex; justify-content: center; align-items: flex-end; gap: 6px; margin: var(--sp-4) 0; }
.step { width: 84px; display: grid; place-items: start center; padding-top: 6px; color: var(--race-text); font-weight: 900; font-size: var(--text-xl); border-radius: 8px 8px 0 0; }
.step--1 { height: 110px; background: var(--podium-gold); }
.step--2 { height: 80px; background: var(--podium-silver); }
.step--3 { height: 60px; background: var(--podium-bronze); }
</style>
